import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Grid,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeGETRequestOnUserService } from "../../Api/api";

interface KYCFormDetailsProps {
    userUUID: string;
}

const Field = ({
    label,
    value,
}: {
    label: string;
    value: any;
}) => (
    <Grid item xs={12} sm={6} md={4}>
        <Box>
            <Typography
                sx={{
                    fontSize: "12px",
                    color: "#777",
                    mb: 0.5,
                }}
            >
                {label}
            </Typography>

            <Typography
                sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#262349",
                    wordBreak: "break-word",
                }}
            >
                {value !== null &&
                    value !== undefined &&
                    value !== ""
                    ? String(value)
                    : "-"}
            </Typography>
        </Box>
    </Grid>
);

const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <Accordion
        defaultExpanded
        sx={{
            mb: 2,
            borderRadius: "8px !important",
            border: "1px solid #e4e5eb",
            boxShadow: "none",
            "&:before": {
                display: "none",
            },
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
                backgroundColor: "#f7f7f9",
                minHeight: 60,
                borderRadius: "8px",
                "&.Mui-expanded": {
                    minHeight: 60,
                    borderBottom: "1px solid #e4e5eb",
                },
                "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                },
            }}
        >
            <Typography
                sx={{
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#262349",
                }}
            >
                {title}
            </Typography>
        </AccordionSummary>

        <AccordionDetails
            sx={{
                p: 2.5,
                backgroundColor: "#fff",
            }}
        >
            {children}
        </AccordionDetails>
    </Accordion>
);

const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    return value;
};

const formatDate = (date: string | null | undefined) => {
    if (!date) {
        return "-";
    }

    try {
        return new Date(date).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return date;
    }
};

const KYCFormDetails = ({
    userUUID,
}: KYCFormDetailsProps) => {
    const [kycData, setKycData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const toastr = require("toastr");

    const fetchKYCForm = async () => {
        if (!userUUID) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const response = await makeGETRequestOnUserService(
                `/mutual_fund/kyc_form/get_by_user/${userUUID}/`
            );

            if (response?.status === 200) {
                setKycData(response?.data);
            } else {
                toastr.error(
                    response?.data?.error ||
                    "Failed to load KYC form details"
                );
            }
        } catch (error: any) {
            console.error(
                "KYC Form Details Error:",
                error
            );

            toastr.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Failed to load KYC form details"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKYCForm();
    }, [userUUID]);

    if (loading) {
        return (
            <Box
                sx={{
                    height: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress
                    sx={{ color: "#262349" }}
                />

                <Typography color="text.secondary">
                    Loading KYC Form Details...
                </Typography>
            </Box>
        );
    }

    if (!kycData) {
        return (
            <Box
                sx={{
                    p: 4,
                    textAlign: "center",
                }}
            >
                <Typography color="text.secondary">
                    No KYC form details available.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                backgroundColor: "#f5f6fa",
                p: { xs: 1, sm: 2 },
            }}
        >
            {/* ================= BASIC DETAILS ================= */}
            <Section title="Basic Details">
                <Grid container spacing={3}>
                    <Field
                        label="KYC Form ID"
                        value={kycData?.id}
                    />

                    <Field
                        label="Object"
                        value={kycData?.object}
                    />

                    <Field
                        label="Type"
                        value={kycData?.type}
                    />

                    <Field
                        label="Status"
                        value={kycData?.status}
                    />

                    <Field
                        label="PAN"
                        value={kycData?.pan}
                    />

                    <Field
                        label="Name"
                        value={kycData?.name}
                    />

                    <Field
                        label="Date of Birth"
                        value={kycData?.date_of_birth}
                    />

                    <Field
                        label="Gender"
                        value={kycData?.gender}
                    />

                    <Field
                        label="Marital Status"
                        value={kycData?.marital_status}
                    />

                    <Field
                        label="Father Name"
                        value={kycData?.father_name}
                    />

                    <Field
                        label="Spouse Name"
                        value={kycData?.spouse_name}
                    />

                    <Field
                        label="Occupation Type"
                        value={kycData?.occupation_type}
                    />

                    <Field
                        label="Income Slab"
                        value={kycData?.income_slab}
                    />

                    <Field
                        label="Residential Status"
                        value={kycData?.residential_status}
                    />

                    <Field
                        label="Place of Birth"
                        value={kycData?.place_of_birth}
                    />

                    <Field
                        label="Country of Birth"
                        value={kycData?.country_of_birth}
                    />

                    <Field
                        label="Nationality Country"
                        value={kycData?.nationality_country}
                    />

                    <Field
                        label="Citizenship Countries"
                        value={
                            kycData?.citizenship_countries?.join(", ")
                        }
                    />
                </Grid>
            </Section>

            {/* ================= CONTACT DETAILS ================= */}
            <Section title="Contact Details">
                <Grid container spacing={3}>
                    <Field
                        label="Email Address"
                        value={kycData?.email_address}
                    />

                    <Field
                        label="ISD"
                        value={kycData?.phone_number?.isd}
                    />

                    <Field
                        label="Phone Number"
                        value={kycData?.phone_number?.number}
                    />
                </Grid>
            </Section>

            {/* ================= IDENTITY & ADDRESS ================= */}
            <Section title="Identity & Address">
                <Grid container spacing={3}>
                    <Field
                        label="Identity Proof Type"
                        value={kycData?.identity?.proof_type}
                    />

                    <Field
                        label="Address Proof Type"
                        value={kycData?.address?.proof_type}
                    />

                    <Field
                        label="Aadhaar Number"
                        value={kycData?.aadhaar_number}
                    />

                    <Field
                        label="PEP Details"
                        value={kycData?.pep_details}
                    />

                    <Field
                        label="Tax Residency Other Than India"
                        value={formatValue(
                            kycData?.tax_residency_other_than_india
                        )}
                    />
                </Grid>
            </Section>

            {/* ================= TAX RESIDENCY ================= */}
            <Section title="Tax Residency Details">
                <Grid container spacing={3}>
                    <Field
                        label="Tax Residency Other Than India"
                        value={formatValue(
                            kycData?.tax_residency_other_than_india
                        )}
                    />

                    <Field
                        label="Non-Indian Tax Residency 1"
                        value={kycData?.non_indian_tax_residency_1}
                    />

                    <Field
                        label="Non-Indian Tax Residency 2"
                        value={kycData?.non_indian_tax_residency_2}
                    />

                    <Field
                        label="Non-Indian Tax Residency 3"
                        value={kycData?.non_indian_tax_residency_3}
                    />
                </Grid>
            </Section>

            {/* ================= KYC STATUS ================= */}
            <Section title="KYC Status">
                <Grid container spacing={3}>
                    <Field
                        label="Signature Provided"
                        value={formatValue(
                            kycData?.signature_provided
                        )}
                    />

                    <Field
                        label="Proof Status"
                        value={kycData?.proof_details?.status}
                    />

                    <Field
                        label="Proof Fetch URL"
                        value={kycData?.proof_details?.fetch_url}
                    />

                    <Field
                        label="Esign Status"
                        value={kycData?.esign_details?.status}
                    />

                    <Field
                        label="Esign URL"
                        value={kycData?.esign_details?.esign_url}
                    />

                    <Grid item xs={12} sm={6} md={4}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "#777",
                                    mb: 0.8,
                                }}
                            >
                                Requirements
                            </Typography>

                            {kycData?.requirements?.fields_needed?.length > 0 ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.8,
                                    }}
                                >
                                    {kycData.requirements.fields_needed.map(
                                        (field: string, index: number) => (
                                            <Chip
                                                key={index}
                                                label={field}
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#f0eff7",
                                                    color: "#262349",
                                                    fontWeight: 500,
                                                    fontSize: "12px",
                                                }}
                                            />
                                        )
                                    )}
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        color: "#262349",
                                    }}
                                >
                                    -
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    <Field
                        label="Reason"
                        value={kycData?.reason}
                    />
                </Grid>
            </Section>

            {/* ================= TIMESTAMPS ================= */}
            <Section title="KYC Timeline">
                <Grid container spacing={3}>
                    <Field
                        label="Created At"
                        value={formatDate(
                            kycData?.created_at
                        )}
                    />

                    <Field
                        label="Updated At"
                        value={formatDate(
                            kycData?.updated_at
                        )}
                    />

                    <Field
                        label="Review Completed At"
                        value={formatDate(
                            kycData?.review_completed_at
                        )}
                    />

                    <Field
                        label="Awaiting Esign At"
                        value={formatDate(
                            kycData?.awaiting_esign_at
                        )}
                    />

                    <Field
                        label="Awaiting Submission At"
                        value={formatDate(
                            kycData?.awaiting_submission_at
                        )}
                    />

                    <Field
                        label="Submitted At"
                        value={formatDate(
                            kycData?.submitted_at
                        )}
                    />

                    <Field
                        label="Failed At"
                        value={formatDate(
                            kycData?.failed_at
                        )}
                    />

                    <Field
                        label="Expires At"
                        value={formatDate(
                            kycData?.expires_at
                        )}
                    />
                </Grid>
            </Section>
        </Box>
    );
};

export default KYCFormDetails;